import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LookupsService } from '@services/lookups.service';
import { TenantService } from '@services/tenant.service';
import { UrlService } from '@services/url.service';
import { SettingSubjectType } from '@shared/models/settings/settings';
import { forkJoin, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  readonly $settingsChanged = new Subject<SettingSubjectType>();

  constructor(
    private http: HttpClient,
    private url: UrlService,
    private lookupsService: LookupsService,
    private tenantService: TenantService,
  ) {}

  getSettings<TSetting>(subject: SettingSubjectType, tenantId?: string): Observable<TSetting | undefined> {
    return this.lookupsService.getSettings<TSetting | undefined>(subject, `tenant:${tenantId ?? 'DEFAULT'}`);
  }

  getSettingsList<TSetting>(subject: SettingSubjectType, attribute?: string): Observable<TSetting[]> {
    const url = this.url.LOOKUPS_URL.replace('{subject}', subject).replace('/{attribute}', '');
    return this.http.get<{ items: Array<{ value: TSetting }> }>(url).pipe(map(result => result.items.map(item => item.value)));
  }

  getSettingsForTenants<TSetting>(subject: SettingSubjectType, tenantIds: string[]): Observable<Record<string, TSetting | undefined>> {
    const allSettingsRequests: Record<string, Observable<TSetting | undefined>> = {};
    for (const tenantId of tenantIds) {
      allSettingsRequests[tenantId] = this.getSettings<TSetting>(subject, tenantId);
    }
    return forkJoin(allSettingsRequests);
  }

  getSettingsForAllTenants<TSetting>(subject: SettingSubjectType): Observable<Record<string, TSetting | undefined>> {
    const allTenantIds = this.tenantService.getAllTenants().map(tenant => tenant.id);
    const allSettingsRequests: Record<string, Observable<TSetting | undefined>> = {};
    for (const tenantId of allTenantIds) {
      allSettingsRequests[tenantId] = this.getSettings<TSetting>(subject, tenantId);
    }
    return forkJoin(allSettingsRequests);
  }

  saveSettings<TSetting>(subject: SettingSubjectType, tenantId: string, settings: TSetting): Observable<TSetting | undefined> {
    return this.lookupsService.setSettings<TSetting>(subject, `tenant:${tenantId}`, settings).pipe(
      map(result => result?.value),
      tap(() => this.$settingsChanged.next(subject)),
    );
  }
}
